/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from "react";

import { Divider } from "antd";
import { useSearchParams } from "react-router";

import { APIContentComponent } from "@companion-core/shared/app/Interfaces/API";
import { BellReferrer, useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import {
  getContentComponentMediaDescriptionAlt,
  getContentComponentMediaUrl,
  getResolvedDescriptionAlt,
} from "@companion-core/shared/app/Utils/componentHelper";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { useWebNavigationHeader } from "@companion-core/web/src/Hooks/navigationHeader";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { Button } from "@companion-core/shared/app/Components/Button";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import { trackClickBell } from "@companion-core/shared/app/tracking/bell";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Tooltip } from "@companion-core/web/src/Components/Tooltip";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { ContentTemplateProps } from "./contentTemplate.model";
import { useContentTemplate } from "../hooks/useContentTemplate";
import { Map } from "@companion-core/shared/app/Components/Map";
import { InlineBell } from "@companion-core/shared/app/Components/Bell/InlineBell";
import { ErrorBoundary } from "@companion-core/shared/app/Components/ErrorBoundary";
import { BellError } from "@companion-core/shared/app/Components/Bell/Error";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { Tags } from "@companion-core/shared/app/Components/Tags/Tags";

const HEIGHT_BREAKPOINT = 250;

export const ContentTemplate = ({ data, contentType }: ContentTemplateProps) => {
  const [params] = useSearchParams();
  const fromOnboarding = useMemo(() => params.get("onboarding") === "true", [params]);
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const {
    googlePlaceData,
    i18n,
    t,
    onBookingCTAVisited,
    bellResolution,
    reportBellFailure,
    initBellFromDynamicPage,
    initDegradedBell,
    bannerImageUrl,
    contentTitle,
    contentSubtitle,
    contentDescription,
    contentConditions,
    contentLegalNotice,
    contentTags,
  } = useContentTemplate(data, fromOnboarding, isMobileSize);
  const { navigateBackward } = useWebNavigationHeader();
  const conciergeModule = getModule("concierge");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [bookingStep, setBookingStep] = useState("booking");
  const [isPopInOpen, setIsPopInOpen] = useState(false);
  const { setIsPopUpVisible, isIntroPopUpVisible, setIsIntroPopUpVisible, setBellReferrer } =
    useBellStore();
  const { publicConfig } = useConfigStore();
  const computedContentType = useMemo(
    () => (contentType ? contentType : data?.content_ct),
    [contentType, data]
  );
  const inlineBellEnabled = useMemo(
    () => conciergeModule?.modules?.inlineBell?.enabled,
    [conciergeModule]
  );

  const logoImageUrl: string | undefined = useMemo(() => {
    if (data)
      return getCloudinaryOptimizedUrl(getContentComponentMediaUrl(data, "logo") ?? "", {
        contentType: "logo",
        width: window.innerWidth,
      });
  }, [data]);
  const logoImageAlt: string | undefined = useMemo(() => {
    if (data) return getContentComponentMediaDescriptionAlt(data, "logo");
  }, [data]);
  const bookingProcessTitle = useMemo(() => {
    if (data)
      return data.booking_process?.type !== "concierge" && data?.type
        ? `common.${computedContentType}.details.cta.${data.type}.online.title`
        : data?.type
          ? `common.${computedContentType}.details.cta.${data.type}.place.title`
          : `common.${computedContentType}.details.cta.place.title`;
  }, [data]);

  const openBell = (): void => {
    trackClickBell("Open_Bell");
    if (["vendor", "offer"].includes(contentType ?? ""))
      setBellReferrer(contentType as BellReferrer);
    if (isIntroPopUpVisible) setIsIntroPopUpVisible(false);
    setIsPopUpVisible(true);
  };

  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const Subtitle = () => {
    if (data?.booking_process?.type != "concierge" && (data?.vendor?.name || contentSubtitle)) {
      return (
        <h2 data-testid="content-details-page__subtitle" className="body-text-4">
          {data?.vendor?.name ?? contentSubtitle ?? ""}
        </h2>
      );
    } else if (data?.vendor?.google_place?.address || data?.google_place?.address) {
      const address =
        data?.vendor?.google_place?.address?.formatted_address ??
        data?.google_place?.address?.formatted_address;
      return (
        <h2
          data-testid="content-details-page__subtitle"
          className="body-text-4 content-details-page__subtitle--address">
          <a href={googlePlaceData?.url ?? ""} target="_blank" rel="noopener noreferrer">
            <FontIcon name="JP-Location" size={24} /> {address as string}
          </a>
        </h2>
      );
    }
  };

  const BookingCTA = ({
    data,
    contentType,
  }: {
    data?: APIContentComponent;
    contentType: string;
  }) => {
    const bookingProcessType = useMemo(
      () => (contentType !== "vendor" ? data?.booking_process?.type : "concierge"),
      [data]
    );
    return (
      <Button
        testID="content-details-page__booking-process--cta"
        size={isMobileSize ? "full" : "large"}
        label={
          i18n.exists(`common.${computedContentType}.details.cta.${bookingProcessType}`)
            ? t(`common.${computedContentType}.details.cta.${bookingProcessType}`)
            : t(`common.${computedContentType}.details.cta.default`)
        }
        icon={
          bookingProcessType && ["concierge", "unique-code"].includes(bookingProcessType)
            ? "JP-Concierge-bell"
            : undefined
        }
        onClick={() => {
          switch (bookingProcessType) {
            case "generic-code":
              onBookingCTAVisited("initial");
              setIsPopInOpen(true);
              break;
            case "unique-code":
              // TODO: make it work
              break;
            case "concierge": {
              reportBellFailure();
              if (bellResolution.outcome === "unavailable") break;
              onBookingCTAVisited();
              const opened =
                bellResolution.outcome === "form" ? initBellFromDynamicPage() : "categories";
              if (opened === "categories") initDegradedBell();
              if (opened === "form" && inlineBellEnabled) {
                setBookingStep("bell");
              } else {
                openBell();
              }
              break;
            }
            default:
              onBookingCTAVisited();
              redirectTo(data?.booking_process?.website ?? "", "external", undefined, "_blank");
              break;
          }
        }}
      />
    );
  };

  return (
    <div className="content-details-page" data-testid="content-details-page">
      <PopIn
        isOpen={isPopInOpen}
        testID="content-details-page-popup"
        content={
          <div className="content-details-page__popin-content">
            <div
              className="content-details-page__popin-content-close"
              data-testid="content-details-page__popin-content-close"
              onClick={() => setIsPopInOpen(false)}>
              <FontIcon name="JP-Close-bold" size={24} />
            </div>
            <span className="content-details-page__popin-content-subtext body-text-6">
              {t("common.offer.details.popup.code.text")}
            </span>
            <div className="content-details-page__popin-content-code">
              <span className="body-text-3">{data?.booking_process?.generic_code}</span>
              <Tooltip
                text={t("common.clipboard.text")}
                direction={isMobileSize ? "left" : "right"}
                timer={3000}>
                <FontIcon
                  className="content-details-page__popin-content-copy-button"
                  name="JP-Copy-document"
                  size={24}
                  onClick={() =>
                    navigator.clipboard.writeText(data?.booking_process?.generic_code ?? "")
                  }
                />
              </Tooltip>
            </div>
          </div>
        }
        primaryButton={{
          text: t(`common.${computedContentType}.details.cta.external-link`),
          size: "full",
          onClick: () => {
            onBookingCTAVisited();
            redirectTo(data?.booking_process?.website ?? "", "external", undefined, "_blank");
          },
        }}
        onClose={() => setIsPopInOpen(false)}
      />

      {bannerImageUrl && (
        <Banner shadowType="gradient" boxShadow imageUrl={bannerImageUrl ?? ""} overlap={false} />
      )}

      {!isMobileSize && (
        <div
          className="content-details-page__back-button-container"
          data-testid="content-details-page__back-button-container">
          <button
            type="button"
            aria-label={t("common.details.accessibility.button.back")}
            onClick={() => navigateBackward()}
            data-testid="content-details-page__back-button"
            className="content-details-page__back-button">
            <FontIcon name="JP-Arrow-left-bold" size={24} aria-hidden={true} />
          </button>
        </div>
      )}

      <div className="content-details-page__layout">
        <div
          className={`content-details-page__content ${bookingStep === "bell" && isMobileSize ? "content-details-page__content--hidden" : ""}`}>
          {contentTitle && (
            <h1
              data-testid="content-details-page__title"
              className="content-details-page__title title-7">
              {contentTitle}
            </h1>
          )}

          <Subtitle />

          {contentTags && <Tags tags={contentTags} className="content-details-page__tags" />}

          {contentDescription && (
            <div
              data-testid="content-details-page__description"
              className="content-details-page__description body-text-6">
              {i18n.exists(`common.${computedContentType}.details.your-privilege.title`) ? (
                <span className="content-details-page__section-title body-text-5">
                  {t(`common.${computedContentType}.details.your-privilege.title`)}
                </span>
              ) : (
                <Divider className="content-details-page__divider" />
              )}
              <div
                className="content-details-page__description-content"
                dangerouslySetInnerHTML={{
                  // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                  __html: sanitizeHtml(contentDescription.replace(/\n/g, "<br>")),
                }}
              />
            </div>
          )}

          {data?.vendor?.name && data.vendor.description && (
            <div data-testid="content-details-page__vendor">
              <Divider className="content-details-page__divider" />
              <span className="content-details-page__section-title body-text-5">
                {data.vendor.name}
              </span>
              <p
                className="content-details-page__vendor-description body-text-6"
                dangerouslySetInnerHTML={{
                  // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                  __html: sanitizeHtml(data.vendor.description.replace(/\n/g, "<br>")),
                }}
              />
            </div>
          )}

          {(contentLegalNotice || contentConditions) && (
            <div data-testid="content-details-page__legal-notice">
              <Divider className="content-details-page__divider" />
              <span className="content-details-page__section-title body-text-5">
                {t(`common.${computedContentType}.details.conditions.title`)}
              </span>
              {contentConditions && (
                <div
                  className="content-details-page__legal-notice-content body-text-6"
                  dangerouslySetInnerHTML={{
                    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                    __html: sanitizeHtml(contentConditions.replace(/\n/g, "<br>")),
                  }}
                />
              )}
              {contentLegalNotice && (
                <div
                  className="content-details-page__legal-notice-content body-text-6"
                  dangerouslySetInnerHTML={{
                    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                    __html: sanitizeHtml(contentLegalNotice.replace(/\n/g, "<br>")),
                  }}
                />
              )}
            </div>
          )}

          {googlePlaceData?.address && publicConfig?.data?.google_maps_web_api_key && (
            <div data-testid="content-details-page__map">
              <div className="content-details-page__map-card">
                <Map
                  beginningAddress={googlePlaceData.address}
                  className="content-details-page__map-wrapper"
                  APIKey={publicConfig?.data?.google_maps_web_api_key}
                />
                <div>
                  <p className="content-details-page__map-addresses">
                    <span className="body-text-6">
                      {googlePlaceData.address?.formatted_address as string}
                    </span>
                  </p>
                  <Button
                    label={t("request.my-requests.recommendation.map-CTA.default")}
                    testID="content-details-page__map-button"
                    type="secondary"
                    onClick={() => redirectTo(googlePlaceData.url ?? "", "external")}
                  />
                </div>
              </div>
            </div>
          )}

          {data?.information && (
            <div data-testid="content-details-page__pratical-information">
              <Divider className="content-details-page__divider" />
              <span className="content-details-page__section-title body-text-5">
                {t(`common.${computedContentType}.details.pratical-information.title`)}
              </span>
              <div
                className="content-details-page__pratical-information-content body-text-6"
                dangerouslySetInnerHTML={{
                  // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                  __html: sanitizeHtml(data.information.replace(/\n/g, "<br>")),
                }}
              />
            </div>
          )}
        </div>

        <div
          data-testid="content-details-page__booking-process"
          className={`content-details-page__booking-wrapper  ${bookingStep === "bell" ? "content-details-page__booking-wrapper--bell" : ""}`}>
          <div
            className={`content-details-page__booking-container ${
              scrollPosition > HEIGHT_BREAKPOINT && bookingStep === "booking" && !isMobileSize
                ? "content-details-page__booking-wrapper--fixed"
                : ""
            } ${bookingStep === "bell" ? "content-details-page__booking-container--bell" : ""}`}>
            {bookingStep === "booking" && (
              <>
                <div className="content-details-page__booking-title">
                  {t(bookingProcessTitle ?? "")}
                </div>
                <div className="content-details-page__booking-detail">
                  {logoImageUrl && (
                    <div
                      data-testid="content-details-page__booking-process--logo"
                      className="content-details-page__booking-logo">
                      <img
                        src={logoImageUrl}
                        alt={getResolvedDescriptionAlt(logoImageAlt, "logo")}
                      />
                    </div>
                  )}
                  <BookingCTA data={data} contentType={computedContentType} />
                </div>
              </>
            )}
            {bookingStep === "bell" && (
              <ErrorBoundary
                context="inline-bell"
                fallback={
                  <BellError
                    className="content-details-page__booking-inline-bell"
                    testID="content-details-page__booking-inline-bell--error"
                    buttonTestID="content-details-page__booking-inline-bell--error-close"
                    buttonLabel={t("common.details.accessibility.button.back")}
                    onClick={() => setBookingStep("booking")}
                  />
                }>
                <InlineBell
                  bellFormPath={bellResolution.formPath ?? ""}
                  className="content-details-page__booking-inline-bell"
                  onClose={() => setBookingStep("booking")}
                />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
